import { PopupBiddingChooseSuit, PopupChooseSuitLayoutType } from "../popup/PopupBiddingChooseSuit";
import { CardData, OptionsItemLiveData, SharedLocData, TableData, TextureData, TrickData } from "../../types/types";
import { Card, CardRank, CardSuit } from "../card/Card";
import { DealerTableToPlayer } from "../components/DealerTableToPlayer";
import { AppLocalization, LanguageId } from "../localization/AppLocalization";
import { Player } from "../player/Player";
import { TablePhase, TableStateMachine, TrumpSuit } from "./TableStateMachine";
import { AppSettings, GamePlayDirection } from "../managers/AppSettings";
import { DealerPlayerToTable } from "../components/DealerPlayerToTable";
import { CardPlayer } from "../components/CardPlayer";
import { TrickCollector } from "../components/TrickCollector";
import { GetCardsValue } from "../utils/GetCardsValue";
import { GetPlayerSide } from "../utils/GetPlayerSide";
import { DeclarationType, PlayerDeclaration } from "../ai/DeclarationsManager";
import { LoaderFileKeys } from "../constant/AppConstants";
import { TrumpSuitToCardSuit } from "../utils/TrumpsSuitToCardSuit";
import { ResultsListItemBubbleColor, ResultsListItemData } from "../screen/dealcomplete/ResultsListItem";
import { DealCompleteContentId } from "../screen/dealcomplete/DealCompleteContentScroll";
import { ContractResult, DealCompleteDealsContent } from "../screen/dealcomplete/DealCompleteDealsContent";
import { DealCompleteTricksContent } from "../screen/dealcomplete/DealCompleteTricksContent";
import { DealCompleteResultsContent } from "../screen/dealcomplete/DealCompleteResultsContent";
import { DealCompleteHandsContent } from "../screen/dealcomplete/DealCompleteHandsContent";
import { GetContractResult } from "../utils/GetContractResult";
import { OptionsDataManager } from "../managers/OptionsDataManager";
import { AppLocalStorage } from "../managers/AppLocalStorage";
import { TableScreen, VariationId } from "../screen/TableScreen";
import { Players } from "../player/Players";
import { PlayerSide, TableAI } from "../ai/TableAI";
import { GetCardSuitIconTextureStatesData, TrumpIconType } from "../utils/GetCardSuitIconTextureData";
import { GetOppositePlayerSide } from "../utils/GetOppositePlayerSide";
import { ButtonBubble } from "../ui/ButtonBubble";
import { OptionsItemKeys } from "../managers/LayoutDataManager";
import { AppDebug, AppDebugLogType } from "../debug/AppDebug";
import { PopupBiddingChoiceType } from "../popup/PopupBiddingTakeSuit";
import { CardSuitToPopupBiddingChoiceType } from "../utils/CardSuitToPopupBiddingChoiceType";
import { AppAudioPlayer, SoundEffectKeys } from "../../audio/AppAudioPlayer";
import { ScreenId } from "../../scenes/GameScene";
import { TableScoreLayout } from "../views/TableScore";
import { SelectedTrumpViewLayout } from "../views/SelectedTrumpView";
import { ScreenTransitionDirection } from "../../managers/ScreenManager";
import { GameCompleteViewLayout } from "../screen/dealcomplete/GameCompleteView";
import { DealCompleteScreenExitType } from "../screen/DealCompleteScreen";
import { GTMTracking } from "../managers/GTMTracking";
import { AppTimings } from "../data/AppConstants";


export enum TaskTypeBelote {
    DEAL_TABLE_TO_PLAYER,
    DEAL_PLAYER_TO_TABLE,
    CHOICE_TAKE_SUIT,
    CHOICE_SELECT_SUIT,
    RESOLVE_PLAY_CARD,

    /**
     * Trick cards will be collected
     * Off the table (into CardsManager)
     */
    COLLECT_CARDS,

    /**
     * Show Deal complete popup, let user to CONTINUE to new Deal .. (or something else?)
     */
    COMPLETE_DEAL,
}

export enum TaskSubTypeBelote {
    /**
     * Default state (?)
     */
    UNDEFINED,

    /**
     * Dealing cards from Table to all Players
     * - can not be interrupted and does not fork
     * - full 'round'
     * - before second round has been dealt
     * - deals fixed amount of cards (3)
     */
    FIRST_ROUND_PLAYER_DEALT,

    /**
     * Dealing cards from Table to all Players
     * - can not be interrupted and does not fork
     * - full 'round'
     * - before faceup card has been picked
     * - deals fixed amount of cards (2)
     */
    SECOND_ROUND_PLAYER_DEALT,

    /**
     * Take faceup card, which determines trump suit
     * - fork (round1): Single choice: ACCEPT | PASS; choosing to accept leads to taking face-up card.
     * - fork (round2): Multiple choice: one of 4 suit choices | NT | AT | PASS; choosing anything except PASS leads to taking face-up card;
     * - first AND second 'round of bidding'
     *   - both 'choosing' and 'picking' leads to the same next Task/State.
     */
    TOOK_FACEUP_CARD,

    /**
     * Deals cards from Table to all Players
     * - can not be interrupted and does not fork
     * - full 'round'
     * - after faceup card has been picked
     * - deals fixed amount of cards (but 1 less to the player that took the faceup card) (2|3)
     */
    THIRD_ROUND_PLAYER_DEALT,
    PLAY_PHASE,
}

export class TableStateMachineBelote extends TableStateMachine {
    /**
     * Card count dealt to each player during bidding phase 1, round 1
     */
    private FIRST_ROUND_CARDS_TO_PLAYER: number = 3;

    /**
     * Card count dealt to each player during bidding phase 1, round 2
     * - this round automatically follows round 1
     */
    private SECOND_ROUND_CARDS_TO_PLAYER: number = 2;

    /**
     * Card count dealt to attacker during bidding phase 2
     * @see this.data.playerAttackerIndex
     */
    private THIRD_ROUND_CARDS_TO_PLAYER_ATTACKER: number = 2;

    /**
     * Card count dealt to each defender and the 
     * 1 remaining player from attacker side during bidding phase 2
     */
    private THIRD_ROUND_CARDS_TO_PLAYER_OTHER: number = 3;


    public override Init(table: TableScreen): void {
        // > table
        super.Init(table);
        // overrides
        this.variationId = VariationId.BELOTE;
    }

    public override GetDataNewGame(variationId: VariationId): TableData {
        const data: TableData = super.GetDataNewGame(variationId);

        // subclass override
        data.state = {
            // for TaskStart, TaskComplete
            taskType: TaskTypeBelote.DEAL_TABLE_TO_PLAYER,
            // for TaskComplete
            taskSubType: TaskSubTypeBelote.FIRST_ROUND_PLAYER_DEALT,
            taskData: {
                playerIndex: data.lastPlayedPlayerIndex,
                cardsCount: this.FIRST_ROUND_CARDS_TO_PLAYER
            }
        }

        return data;
    }

    public override SetDataNextDeal(): void {
        super.SetDataNextDeal(false); // save?

        this.data.state = {
            taskType: TaskTypeBelote.DEAL_TABLE_TO_PLAYER,
            taskSubType: TaskSubTypeBelote.FIRST_ROUND_PLAYER_DEALT,
            taskData: {
                playerIndex: this.data.lastPlayedPlayerIndex,
                cardsCount: this.FIRST_ROUND_CARDS_TO_PLAYER
            }
        };

        AppLocalStorage.SetTableData(this.variationId, this.data);
    }
    
    public override TaskStart(taskType: TaskTypeBelote, taskData: any = undefined): void {
        // console.log(`TaskStart; taskType=${TaskTypeBelote[taskType]}, taskData=`,taskData);

        const playDirection: GamePlayDirection = this.data.options.gamePlayDirection;
        let disabledChoices: PopupBiddingChoiceType[];
        let tableCard: Card;
        switch (taskType) {
            case TaskTypeBelote.DEAL_TABLE_TO_PLAYER:
                const dealerTableToPlayer: DealerTableToPlayer = this.table.components.GetComponent(this.table, DealerTableToPlayer);
                dealerTableToPlayer.SetData({
                    taskType: TaskTypeBelote.DEAL_TABLE_TO_PLAYER,
                    onComplete: this.OnTaskComplete.bind(this),
                    playerIndex: taskData.playerIndex,
                    cardsCount: taskData.cardsCount,
                });
                dealerTableToPlayer.Deal();
                break;

            case TaskTypeBelote.CHOICE_TAKE_SUIT:
                // live player deciding to take or not take faceup card
                // First player to bid can't pass on Jack
                // When this option is selected and the face-up card is Jack, the first player to bid can't pass.
                const cantPassOnJack: OptionsItemLiveData = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.passOnJack, this.table.variationId);
                const isCantPassOnJack: boolean = (cantPassOnJack && (cantPassOnJack.value as boolean));
                disabledChoices = [];
                tableCard = this.table.tableStackInit.GetLast();
                if (isCantPassOnJack) {
                    if (this.table.tableStackInit.GetLast().rank == CardRank.JACK) {
                        disabledChoices.push(PopupBiddingChoiceType.PASS);
                    }
                }
                this.table.GetTableAI().ResolveTakeSuitChoice(disabledChoices, tableCard);
                if (this.data.lastPlayedPlayerIndex == Players.PLAYER_INDEX) {
                    const enableGameTips: OptionsItemLiveData = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.enableGameTips, this.table.variationId);
                    const isEnableGameTips: boolean = enableGameTips && (enableGameTips.value as boolean);
                    const autoShowGameTips: OptionsItemLiveData = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.autoShowGameTips, this.table.variationId);
                    const isAutoShowGameTips: boolean = autoShowGameTips && (autoShowGameTips.value as boolean);

                    this.table.popupChoiceTakeSuit.SetData({
                        taskType: TaskTypeBelote.CHOICE_TAKE_SUIT,
                        topCard: tableCard, // for UI 
                        onComplete: this.OnTaskComplete.bind(this),
                        disabledChoices: disabledChoices,
                    }
                    );
                    this.table.popupChoiceTakeSuit.Show();

                    if (isEnableGameTips) {
                        // set hint text
                        this.table.popupHint.SetText(this.table.GetTableAI().GetHintText());
                        // toggle on/off tips button
                        this.table.btnHints.SetSelected(isAutoShowGameTips);
                        // enable tips button
                        this.table.btnHints.SetInteractive(true);
                        if (isAutoShowGameTips) {
                            // show hints
                            this.table.popupHint.Show();
                        }
                    }
                }
                // else AI deciding to take or not take faceup card
                else {
                    this.table.GetTableAI().TaskResolveTakeSuit(
                        {
                            taskType: TaskTypeBelote.CHOICE_TAKE_SUIT,
                            onComplete: this.OnTaskComplete.bind(this),
                            playerIndex: this.data.lastPlayedPlayerIndex,
                            disabledChoices: disabledChoices,
                        }
                    );
                }
                break;

            case TaskTypeBelote.CHOICE_SELECT_SUIT:
                disabledChoices = [];
                tableCard = this.table.tableStackInit.GetLast();
                const enableNTAT: OptionsItemLiveData = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.enableNTAT, this.table.variationId);
                const isEnableNTAT: boolean = enableNTAT && (enableNTAT.value as boolean);
                // if Vache: CAN NOT PASS; CAN select face-up suit
                const variantVache: OptionsItemLiveData = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.variantVache, this.table.variationId);
                const isVariantVache: boolean = (variantVache && (variantVache.value as boolean));
                // If Vache
                if (isVariantVache) {
                    // if current player is Dealer
                    if (this.data.dealDealerIndex == this.data.lastPlayedPlayerIndex) {
                        // Dealer can not PASS
                        disabledChoices.push(PopupBiddingChoiceType.PASS);
                    }
                }
                // If NOT Vache:
                // - CAN PASS
                // - CAN NOT select FACEUP SUIT
                else {
                    disabledChoices.push(CardSuitToPopupBiddingChoiceType(this.table.tableStackInit.GetLast().suit));
                }

                if (!isEnableNTAT) {
                    disabledChoices.push(PopupBiddingChoiceType.NOTRUMPS);
                    disabledChoices.push(PopupBiddingChoiceType.ALLTRUMPS);
                }

                // Player or Bot decides if to choose a suit (based on cards on hand etc) or PASS
                this.table.GetTableAI().ResolveChooseSuitChoice(disabledChoices, tableCard);

                // if Player, show Choose suit popup
                if (this.data.lastPlayedPlayerIndex == Players.PLAYER_INDEX) {
                    const enableGameTips: OptionsItemLiveData = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.enableGameTips, this.table.variationId);
                    const isEnableGameTips: boolean = enableGameTips && (enableGameTips.value as boolean);
                    const autoShowGameTips: OptionsItemLiveData = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.autoShowGameTips, this.table.variationId);
                    const isAutoShowGameTips: boolean = autoShowGameTips && (autoShowGameTips.value as boolean);
                    const popupChoiceChooseSuit = this.table.popupChoiceChooseSuit as PopupBiddingChooseSuit;

                    popupChoiceChooseSuit.SetLayoutType(isEnableNTAT ?
                        PopupChooseSuitLayoutType.WITH_NT_AT :
                        PopupChooseSuitLayoutType.NO_NT_AT
                    );

                    popupChoiceChooseSuit.SetData({
                        taskType: TaskTypeBelote.CHOICE_SELECT_SUIT,
                        onComplete: this.OnTaskComplete.bind(this),
                        disabledChoices: disabledChoices,
                    });
                    // > show/hide buttons based on disabledChocies
                    // > sets bg size and position based on UI visibility
                    popupChoiceChooseSuit.UpdateView();
                    popupChoiceChooseSuit.Show();

                    if (isEnableGameTips) {
                        // set hint text
                        this.table.popupHint.SetText(this.table.GetTableAI().GetHintText());
                        // toggle on/off tips button
                        this.table.btnHints.SetSelected(isAutoShowGameTips);
                        // enable tips button
                        this.table.btnHints.SetInteractive(true);
                        if (isAutoShowGameTips) {
                            // show hints
                            this.table.popupHint.Show();
                        }
                    }
                }
                // else AI chooses suit (or PASS) already processed
                else {
                    this.table.GetTableAI().TaskResolveChooseSuit({
                        taskType: TaskTypeBelote.CHOICE_SELECT_SUIT,
                        onComplete: this.OnTaskComplete.bind(this),
                        playerIndex: this.data.lastPlayedPlayerIndex,
                        disabledChoices: disabledChoices,
                    });
                    // this.OnTaskComplete(TaskTypeBelote.CHOICE_SELECT_SUIT, {
                    //     choiceType: this.lastBidChoice.choiceType,
                    // });
                }
                break;

            // PLAY_PHASE only
            case TaskTypeBelote.RESOLVE_PLAY_CARD:
                const playerIndex: number = this.data.lastPlayedPlayerIndex;
                const firstPlayerInRound: boolean = playerIndex == this.data.trickDealerIndex;
                const hasFullHand = this.table.players.GetPlayerByIndex(playerIndex).GetCards().length == 8;
                // first player in Deal to play
                if (hasFullHand && firstPlayerInRound) {
                    // cache declarations (for later)
                    const declarations: PlayerDeclaration[] = this.table.declarationsManager.ResolvePlayersDeclarations();
                    this.data.declarations = declarations;
                    // show score/trump views and CARD POINTS
                    this.OnFirstTrickStart();
                }
                // live player deciding which card to play
                if (playerIndex == Players.PLAYER_INDEX) {
                    // resolve playableCards
                    // resolve suggestedCards
                    // set Hint text
                    const player: Player = this.table.players.GetPlayerByIndex(playerIndex);
                    this.table.GetTableAI().ResolvePlayableAndSuggestedCards(playerIndex);
                    const playableCards: Card[] = this.table.GetTableAI().GetPlayableCards();
                    const suggestedCards: Card[] = this.table.GetTableAI().GetSuggestedCards();
                    const autoPlay: OptionsItemLiveData = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.autoPlay, this.table.variationId);
                    const isAutoPlay: boolean = autoPlay && (autoPlay.value as boolean);
                    // if autoplay last card in live player's hands
                    if (isAutoPlay && (playableCards.length == 1)) {
                        // play cards task directly
                        // NOTE: we do not RESTORE POINT here, 
                        //       because we cant save card:Card
                        this.TaskStart(
                            TaskTypeBelote.DEAL_PLAYER_TO_TABLE,
                            {
                                card: playableCards[0]
                            }
                        );
                    }
                    // else play manually
                    else {
                        // get ref
                        const popupPlayableCards: OptionsItemLiveData = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.popupPlayableCards, this.table.variationId);
                        const isPopupPlayableCards: boolean = popupPlayableCards && (popupPlayableCards.value as boolean);
                        const enableGameTips: OptionsItemLiveData = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.enableGameTips, this.table.variationId);
                        const isEnableGameTips: boolean = enableGameTips && (enableGameTips.value as boolean);
                        const autoShowGameTips: OptionsItemLiveData = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.autoShowGameTips, this.table.variationId);
                        const isAutoShowGameTips: boolean = autoShowGameTips && (autoShowGameTips.value as boolean);
                        const cardPlayer: CardPlayer = this.table.components.GetComponent(this.table, CardPlayer);

                        // player choosing play card
                        // schedule a wakeup animation (focus animation) to let player know its their turn
                        this.table.players.GetPlayerByIndex(Players.PLAYER_INDEX).ScheduleWakeupAnimation();

                        cardPlayer.SetData({
                            taskType: TaskTypeBelote.RESOLVE_PLAY_CARD,
                            onComplete: this.OnTaskComplete.bind(this),
                            playerIndex: playerIndex,
                        });

                        // cards interactivity
                        cardPlayer.UpdateCardInteractivity(playableCards);

                        // popup playable
                        if (isPopupPlayableCards) {
                            cardPlayer.PopPlayable(playableCards);
                        }
                        // update hints position (regardless of popup playable)
                        this.table.popupHint.OnResize();

                        if (isEnableGameTips) {
                            // set hint text
                            this.table.popupHint.SetText(this.table.GetTableAI().GetHintText());
                            // toggle on/off tips button
                            this.table.btnHints.SetSelected(isAutoShowGameTips);
                            // enable tips button
                            this.table.btnHints.SetInteractive(true);
                            // auto show tips
                            if (isAutoShowGameTips) {
                                // hint toggle button ON = show highlights
                                cardPlayer.HighlightCards(suggestedCards, playerIndex);
                                // if (this.GetTablePhase() != TablePhase.BIDDING) {
                                //     cardPlayer.HighlightCards(suggestedCards, playerIndex);
                                // }
                                // show hints
                                this.table.popupHint.Show();
                            }
                            else {
                                // hint toggle button OFF = no highlights
                                cardPlayer.HighlightCards(player.GetCards(), playerIndex);
                                // if (this.GetTablePhase() != TablePhase.BIDDING) {
                                //     cardPlayer.HighlightCards(player.GetCards(), playerIndex);
                                // }
                            }
                        }
                        else {
                            // toggle on/off tips button
                            this.table.btnHints.SetSelected(false);
                            // enable tips button
                            this.table.btnHints.SetInteractive(false);
                            // hint toggle button DISABLED = no highlights
                            cardPlayer.HighlightCards(player.GetCards(), playerIndex);
                        }
                    }
                }
                // else AI deciding which card to play
                else {
                    // > ResolvePlayableAndSuggestedCards, return random.
                    this.table.GetTableAI().TaskResolvePlayCard({
                        taskType: TaskTypeBelote.RESOLVE_PLAY_CARD,
                        onComplete: this.OnTaskComplete.bind(this),
                        playerIndex: playerIndex,
                    });
                }
                break;

            case TaskTypeBelote.DEAL_PLAYER_TO_TABLE:
                const dealerPlayerToTable: DealerPlayerToTable = this.table.components.GetComponent(this.table, DealerPlayerToTable);
                dealerPlayerToTable.SetData({
                    taskType: TaskTypeBelote.DEAL_PLAYER_TO_TABLE,
                    onComplete: this.OnTaskComplete.bind(this),
                    playerIndex: this.data.lastPlayedPlayerIndex,
                    cards: [taskData.card],
                });
                dealerPlayerToTable.Deal();
                break;

            case TaskTypeBelote.COLLECT_CARDS:
                this.StartCollectCards(TaskTypeBelote.COLLECT_CARDS);
                break;

            case TaskTypeBelote.COMPLETE_DEAL:
                this.StartDealComplete(TaskTypeBelote.COMPLETE_DEAL);
                break;

            default:
                console.warn("TaskStart: Unrecognized taskType:", TaskTypeBelote[taskType]);
        }
    }

    public override OnTaskComplete(taskType: TaskTypeBelote, taskData: any = undefined): void {
        // console.log(`OnTaskComplete; taskType=${TaskTypeBelote[taskType]}, taskSubType=${TaskSubTypeBelote[this.data.state.taskSubType]}, taskData=`,taskData);

        const playDirection: GamePlayDirection = this.data.options.gamePlayDirection;
        let lastPlayerIndex: number;
        let nextPlayedPlayerIndex: number;
        switch (taskType) {
            case TaskTypeBelote.DEAL_TABLE_TO_PLAYER:
                // NOTE: table to player ALWAYS changes card amount in: tableStackInit, players hands
                // post-task caching:
                // - update initStackCards
                this.data.initStackCards = this.table.tableStackInit.GetCardDatas();
                // - update playersCardsData
                this.data.playersCardsData = this.table.players.GetPlayersCardDatas();

                // update state vars
                lastPlayerIndex = this.data.lastPlayedPlayerIndex;
                nextPlayedPlayerIndex = this.GetFixedPlayerIndex(lastPlayerIndex + playDirection);
                this.data.lastPlayedPlayerIndex = nextPlayedPlayerIndex;

                // One of the players was just dealt card(s).
                // - this is first round of dealing cards.
                if (this.data.state.taskSubType == TaskSubTypeBelote.FIRST_ROUND_PLAYER_DEALT) {
                    // not made whole round yet
                    if (this.data.trickDealerIndex != lastPlayerIndex) {
                        // capture restore point
                        this.data.state = {
                            taskType: TaskTypeBelote.DEAL_TABLE_TO_PLAYER,
                            taskSubType: TaskSubTypeBelote.FIRST_ROUND_PLAYER_DEALT,
                            taskData: {
                                playerIndex: nextPlayedPlayerIndex,
                                cardsCount: this.FIRST_ROUND_CARDS_TO_PLAYER
                            }
                        }
                        // save restore point immediately to localStorage
                        AppLocalStorage.SetTableData(this.variationId, this.data);
                        // run task
                        this.TaskStart(
                            this.data.state.taskType,
                            {
                                playerIndex: this.data.state.taskData.playerIndex,
                                cardsCount: this.data.state.taskData.cardsCount
                            }
                        );
                    }
                    // dealer dealt to itself, round complete
                    else {
                        // capture restore point
                        this.data.state = {
                            taskType: TaskTypeBelote.DEAL_TABLE_TO_PLAYER,
                            taskSubType: TaskSubTypeBelote.SECOND_ROUND_PLAYER_DEALT,
                            taskData: {
                                playerIndex: nextPlayedPlayerIndex,
                                cardsCount: this.SECOND_ROUND_CARDS_TO_PLAYER
                            },
                        }
                        // save restore point immediately to localStorage
                        AppLocalStorage.SetTableData(this.variationId, this.data);
                        // run task
                        this.TaskStart(
                            this.data.state.taskType,
                            {
                                playerIndex: this.data.state.taskData.playerIndex,
                                cardsCount: this.data.state.taskData.cardsCount
                            }
                        );
                    }
                }
                // One of the players was just dealt card(s).
                // - this is second round of dealing cards.
                else if (this.data.state.taskSubType == TaskSubTypeBelote.SECOND_ROUND_PLAYER_DEALT) {
                    // not made whole round yet
                    if (this.data.trickDealerIndex != lastPlayerIndex) {
                        // capture restore point
                        this.data.state = {
                            taskType: TaskTypeBelote.DEAL_TABLE_TO_PLAYER,
                            taskSubType: TaskSubTypeBelote.SECOND_ROUND_PLAYER_DEALT,
                            taskData: {
                                playerIndex: nextPlayedPlayerIndex,
                                cardsCount: this.SECOND_ROUND_CARDS_TO_PLAYER
                            }
                        }
                        // save restore point immediately to localStorage
                        AppLocalStorage.SetTableData(this.variationId, this.data);
                        // run task
                        this.TaskStart(
                            this.data.state.taskType,
                            {
                                playerIndex: this.data.state.taskData.playerIndex,
                                cardsCount: this.data.state.taskData.cardsCount
                            }
                        );
                    }
                    // dealer dealt to itself, round complete
                    else {
                        // flip top stack Card
                        const cardFlipDuration = OptionsDataManager.GetTiming(AppTimings.cardFlipDuration);
                        const cardFlipNextTaskDelay = OptionsDataManager.GetTiming(AppTimings.cardFlipNextTaskDelay);
                        this.table.tableStackInit.GetLast().Flip(true, cardFlipDuration);
                        // present choice popup: TAKESUIT (or quick-resolve for BOT)
                        this.table.scene.time.delayedCall(
                            cardFlipNextTaskDelay,
                            () => {
                                // capture restore point
                                this.data.state = {
                                    taskType: TaskTypeBelote.CHOICE_TAKE_SUIT,
                                    taskSubType: TaskSubTypeBelote.SECOND_ROUND_PLAYER_DEALT, // shouldnt matter
                                    // taskData: {}
                                }
                                // save restore point immediately to localStorage
                                AppLocalStorage.SetTableData(this.variationId, this.data);
                                // run task
                                this.TaskStart(
                                    this.data.state.taskType
                                );
                            },
                            [],     // args
                            this    // callbackScope
                        );
                    }
                }
                // Faceup card was TAKEN from initStack by player
                // - this will only trigger once per bidding phase
                // - restart dealing from {dealer + 1}
                // - start task dealing to each player up to 8 cards in hand (2/3) starting from dealer+1
                else if (this.data.state.taskSubType == TaskSubTypeBelote.TOOK_FACEUP_CARD) {
                    this.data.state = {
                        taskType: TaskTypeBelote.DEAL_TABLE_TO_PLAYER,
                        taskSubType: TaskSubTypeBelote.THIRD_ROUND_PLAYER_DEALT,
                        taskData: {
                            playerIndex: nextPlayedPlayerIndex,
                            cardsCount: nextPlayedPlayerIndex == this.data.playerAttackerIndex ?
                                this.THIRD_ROUND_CARDS_TO_PLAYER_ATTACKER :
                                this.THIRD_ROUND_CARDS_TO_PLAYER_OTHER,
                        }
                    }
                    // save restore point immediately to localStorage
                    AppLocalStorage.SetTableData(this.variationId, this.data);
                    // start task
                    this.TaskStart(
                        this.data.state.taskType,
                        {
                            playerIndex: this.data.state.taskData.playerIndex,
                            cardsCount: this.data.state.taskData.cardsCount,
                        }
                    );
                }
                // One of the players was just dealt card(s).
                // - this is third round of dealing cards.
                else if (this.data.state.taskSubType == TaskSubTypeBelote.THIRD_ROUND_PLAYER_DEALT) {
                    const cardsCount = nextPlayedPlayerIndex == this.data.playerAttackerIndex ?
                        this.THIRD_ROUND_CARDS_TO_PLAYER_ATTACKER :
                        this.THIRD_ROUND_CARDS_TO_PLAYER_OTHER;
                    // not made whole round yet
                    if (this.data.trickDealerIndex != lastPlayerIndex) {
                        // capture restore point
                        this.data.state = {
                            taskType: TaskTypeBelote.DEAL_TABLE_TO_PLAYER,
                            taskSubType: TaskSubTypeBelote.THIRD_ROUND_PLAYER_DEALT,
                            taskData: {
                                playerIndex: nextPlayedPlayerIndex,
                                cardsCount: cardsCount
                            }
                        }
                        // save restore point immediately to localStorage
                        AppLocalStorage.SetTableData(this.variationId, this.data);
                        // start task
                        this.TaskStart(
                            this.data.state.taskType,
                            {
                                playerIndex: this.data.state.taskData.playerIndex,
                                cardsCount: this.data.state.taskData.cardsCount,
                            }
                        );
                    }
                    // dealer dealt to itself, round complete
                    else {
                        // update state vars
                        this.data.trickDealerIndex = nextPlayedPlayerIndex;
                        // historyHandsData (unique single store per Deal)
                        this.data.historyHandsData = this.table.players.GetPlayersCardDatas();
                        // capture restore point
                        this.data.state = {
                            taskType: TaskTypeBelote.RESOLVE_PLAY_CARD,
                            taskSubType: TaskSubTypeBelote.PLAY_PHASE,
                        }
                        // save restore point immediately to localStorage
                        AppLocalStorage.SetTableData(this.variationId, this.data);
                        // console.log(JSON.stringify(this.data));
                        // console.log("check data");
                        // return;

                        // wait before you play phase, if cardDealAnimation=FALSE
                        // this will allow ReorderCards to finish its animation
                        // (as to not conflict with card dealing positional animation)
                        const cardReorderDuration = OptionsDataManager.GetTiming(2 * AppTimings.cardReorderDuration + 100);
                        this.table.scene.time.delayedCall(cardReorderDuration, () => {
                            // start task
                            this.TaskStart(this.data.state.taskType);
                        });
                    }
                }
                break;

            case TaskTypeBelote.CHOICE_TAKE_SUIT:
                // console.log("CHOICE_TAKE_SUIT");
                if (this.data.lastPlayedPlayerIndex == Players.PLAYER_INDEX) {
                    if (this.table.popupHint.IsShowing()) {
                        this.table.popupHint.Hide();
                    }

                    // toggle off tips button
                    this.table.btnHints.SetSelected(false);
                    // disable tips button
                    this.table.btnHints.SetInteractive(false);
                    // hide tips
                    this.table.popupHint.Hide();
                }
                this.ResolvePlayerTakeSuitChoice(taskData.choiceType as PopupBiddingChoiceType);
                break;

            case TaskTypeBelote.CHOICE_SELECT_SUIT:
                // console.log("CHOICE_SELECT_SUIT");
                if (this.data.lastPlayedPlayerIndex == Players.PLAYER_INDEX) {
                    if (this.table.popupHint.IsShowing()) {
                        this.table.popupHint.Hide();
                    }

                    // toggle off tips button
                    this.table.btnHints.SetSelected(false);
                    // disable tips button
                    this.table.btnHints.SetInteractive(false);
                    // hide tips
                    this.table.popupHint.Hide();
                }
                this.ResolvePlayerChooseSuitChoice(taskData.choiceType as PopupBiddingChoiceType);
                break;

            /**
            taskData:
            {
                card:Card,
            }
            */
            case TaskTypeBelote.RESOLVE_PLAY_CARD:
                const playerIndex: number = this.data.lastPlayedPlayerIndex;
                // if live Player chose card to play
                if(playerIndex === Players.PLAYER_INDEX) {
                    this.table.players.GetPlayerByIndex(Players.PLAYER_INDEX).ClearWakeupAnimation(true);

                    // toggle off tips button
                    this.table.btnHints.SetSelected(false);
                    // disable tips button
                    this.table.btnHints.SetInteractive(false);
                    // hide tips
                    this.table.popupHint.Hide();
                }


                // call play cards task directly
                // NOTE: we do not RESTORE POINT here, 
                //       because we cant save card:Card
                this.TaskStart(
                    TaskTypeBelote.DEAL_PLAYER_TO_TABLE,
                    {
                        card: taskData.card
                    }
                );
                break;

            /*
                taskType: TaskTypeBelote.DEAL_PLAYER_TO_TABLE,
                onComplete: this.OnTaskComplete.bind(this),
                playerIndex: taskData.playerIndex,
                cards: taskData.cards,
            */
            case TaskTypeBelote.DEAL_PLAYER_TO_TABLE:
                // console.log("complete: DEAL_PLAYER_TO_TABLE");
                // post-task caching:
                // - update playStackCards
                this.data.playStackCards = this.table.tableStackPlay.GetCardDatas();
                // - update playersCardsData
                this.data.playersCardsData = this.table.players.GetPlayersCardDatas();
                // deal next player until reached dealerIndex, then GO PLAY PHASE.
                lastPlayerIndex = this.data.lastPlayedPlayerIndex;
                nextPlayedPlayerIndex = this.GetFixedPlayerIndex(lastPlayerIndex + playDirection);
                this.data.lastPlayedPlayerIndex = nextPlayedPlayerIndex;
                // nextPlayedPlayerIndex == this.data.trickDealerIndex
                // 1,1,0 -> trickDealerIndex starts dealing the round, one before him is last that deals.
                // console.log(this.data.trickDealerIndex, lastPlayerIndex, nextPlayedPlayerIndex);
                // console.log("nextPlayedPlayerIndex",nextPlayedPlayerIndex,"cardsCount",cardsCount);

                // regardless of which player in trick dealt right now, 
                // inject simple declarations here
                this.table.GetTableAI().ResolveMinorDeclarationsAfterPlayerPlayedCard(lastPlayerIndex, () => {
                    // console.log(this.data.trickDealerIndex, nextPlayedPlayerIndex);
                    // done with simple declarations
                    // not made whole round yet
                    if (this.data.trickDealerIndex != nextPlayedPlayerIndex) {
                        // capture restore point
                        this.data.state = {
                            taskType: TaskTypeBelote.RESOLVE_PLAY_CARD,
                            taskSubType: TaskSubTypeBelote.PLAY_PHASE,
                            taskData: {
                                playerIndex: nextPlayedPlayerIndex,
                            }
                        }
                        // save restore point immediately to localStorage
                        AppLocalStorage.SetTableData(this.variationId, this.data);
                        // start task
                        this.TaskStart(
                            this.data.state.taskType,
                            {
                                playerIndex: this.data.state.taskData.playerIndex,
                            }
                        );
                    }
                    // dealer dealt to itself, round complete (trick over)
                    else {
                        // automatically (or manually) collect the trick cards 
                        const onComplete = () => {
                            // capture restore point
                            this.data.state = {
                                taskType: TaskTypeBelote.COLLECT_CARDS,
                                taskSubType: TaskSubTypeBelote.PLAY_PHASE,
                            }
                            // save restore point immediately to localStorage
                            AppLocalStorage.SetTableData(this.variationId, this.data);
                            // start task
                            this.TaskStart(
                                this.data.state.taskType,
                            );
                        }
                        // automatically collect trickcards, or
                        // click OK to collect trick cards
                        const autoCollectTricks: OptionsItemLiveData = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.autoCollectTricks, this.table.variationId);
                        if (autoCollectTricks && autoCollectTricks.value) {
                            onComplete();
                        }
                        else {
                            const accepterBubble: ButtonBubble = this.table.players.GetPlayerByIndex(Players.PLAYER_INDEX).acceptorBubble;
                            accepterBubble.SetButtonLabel(AppLocalization.GetSharedData().table.acceptPopup.btnPrimary.itemLabel);
                            accepterBubble.UpdateView();
                            accepterBubble.SetData({ onComplete: onComplete });
                            accepterBubble.Show();
                        }
                    }
                });
                break;

            // collected trick cards
            // A) play next trick (4xCHOICE_PLAY_CARD + 4xDEAL_PLAYER_TO_TABLE), or
            // B) (if player's hand empty) COMPLETE_ROUND
            case TaskTypeBelote.COLLECT_CARDS:
                // post-task caching:
                // - update playStackCards
                this.data.playStackCards = this.table.tableStackPlay.GetCardDatas();
                // players have on-hand cards
                const playerCardsCount = this.table.players.GetPlayerByIndex(0).GetCards().length;
                if (playerCardsCount > 0) {
                    // if first round trick over
                    const firstTrickJustFinished: boolean = playerCardsCount == 7;
                    // console.log("firstTrickJustFinished",firstTrickJustFinished);
                    if (firstTrickJustFinished) {
                        // if got major declaration
                        const majorDeclarations: PlayerDeclaration[] = this.data.declarations?.filter(d => d.declarationType != DeclarationType.BELOTE_REBELOTE);
                        majorDeclarations.forEach(d=>{
                            d.declarationCards.played.forEach((p,i,a)=>{
                                a[i] = true;
                            });
                            this.AddDealBonusPoints(d);
						});

                        this.table.declarationsManager.InjectDeclarationAnimations(
                            majorDeclarations.slice(),
                            this.ResolvePlayCard.bind(this,
                                TaskTypeBelote.RESOLVE_PLAY_CARD,
                                TaskSubTypeBelote.PLAY_PHASE
                            )
						);
                    }
                    // first trick didnt just finish
                    else {
                        this.ResolvePlayCard(
                            TaskTypeBelote.RESOLVE_PLAY_CARD,
                            TaskSubTypeBelote.PLAY_PHASE
                        );
                    }
                }
                // players have no more cards
                // this was last trick. 
                // show 'Deal complete' popup
                else {
                    // EDIT: we dont save this state as restore pint
                    // start task
                    this.TaskStart(
                        TaskTypeBelote.COMPLETE_DEAL,
                    );
                }
                break;

            // dealComplete popup was closed; process user choice (taskData);
            case TaskTypeBelote.COMPLETE_DEAL:
                // in expectation of possible 'game end'
                // NOTE: we do consider both player sides (in case max score was changed mid-game)
                const pointsPerGame: number = OptionsDataManager.GetPointsPerGame();
                let pointsPerGameReached = this.data.gameTotalScoreUs >= pointsPerGame || this.data.gameTotalScoreThem >= pointsPerGame;
                const isTie: boolean = this.data.gameTotalScoreUs == this.data.gameTotalScoreThem;
                const isGameOver = (pointsPerGameReached && !isTie);
                // console.log(DealCompleteScreenExitType[taskData.exitType], "isGameOver", isGameOver)

                switch (taskData.exitType) {
                    case DealCompleteScreenExitType.CONTINUE:
                        // deal complete, save stats
                        this.SaveStatsDealComplete();
                        // 
                        const prevDealerIndex = this.data.dealDealerIndex;
                        const nextDealerIndex = this.GetFixedPlayerIndex(prevDealerIndex + playDirection);
                        // hide table content
                        this.table.HideContent();
                        // reset table (pool cards, etc)
                        this.table.Reset();
                        // set stateMachine data
                        this.SetDataNextDeal();
                        // build table view from said data
                        this.UpdateViewFromData();
                        // snap chip to prev player
                        this.table.dealerChipController.SetDealerIndex(prevDealerIndex);
                        // fade in table contents from state
                        // NOTE: OnShow also snaps chip to player with index we set previously.
                        this.table.GetScene().dealCompleteScreen.transitionDirection = ScreenTransitionDirection.CENTER_TO_LEFT;
                        this.table.GetScene().dealCompleteScreen.Hide();
                        this.table.transitionDirection = ScreenTransitionDirection.RIGHT_TO_CENTER;
                        this.table.Show(true, () => {
                            // animate chip from prev player to next player
                            this.table.dealerChipController.SnapAnimateFromTo(prevDealerIndex, nextDealerIndex, () => {
                                this.Start(false, false);
                            });
                        });
                        break;
                    case DealCompleteScreenExitType.LOBBY:
                        if (isGameOver) {
                            // deal complete, save stats
                            this.SaveStatsDealComplete();
                            // game complete, save stats
                            this.SaveStatsGameComplete();
                            // game complete, wipe table data
                            this.DeleteTableData();
                        }
                        this.table.scene.tweens.killAll();
                        this.table.scene.time.removeAllEvents();
		                this.table.scene.time.clearPendingEvents();
                        
                        AppAudioPlayer.PlaySfx(SoundEffectKeys.click5);
                        AppLocalStorage.SetLastVisitedScreenId(ScreenId.LOBBY);

                        this.table.GetScene().dealCompleteScreen.transitionDirection = ScreenTransitionDirection.CENTER_TO_RIGHT;
                        this.table.GetScene().dealCompleteScreen.Hide();
                        this.table.GetScene().lobbyScreen.transitionDirection = ScreenTransitionDirection.LEFT_TO_CENTER;
                        this.table.GetScene().lobbyScreen.Show();
                        break;

                    case DealCompleteScreenExitType.NEW_GAME:
                        // deal complete, save stats
                        this.SaveStatsDealComplete();
                        // deal complete, save stats
                        this.SaveStatsGameComplete();
                        // hide table content
                        this.table.HideContent();
                        // reset table (pool cards, etc)
                        this.table.Reset();
                        // set stateMachine data
                        this.GetSetDataNewGame(this.variationId);
                        // build table view from said data
                        this.UpdateViewFromData();
                        // fade in table contents from state
                        this.table.GetScene().dealCompleteScreen.transitionDirection = ScreenTransitionDirection.CENTER_TO_LEFT;
                        this.table.GetScene().dealCompleteScreen.Hide();
                        this.table.transitionDirection = ScreenTransitionDirection.RIGHT_TO_CENTER;
                        this.table.Show(true, () => {
                            // start next game (Task)
                            this.Start(false, false);
                        });
                        break;
                }
                break;

            default:
                console.warn("OnTaskComplete: Unrecognized taskType:", TaskTypeBelote[taskType]);
        }
    }
    
    protected ResolvePlayerTakeSuitChoice(choiceType: PopupBiddingChoiceType): void {
        // console.log("ResolvePlayerTakeSuitChoice", PopupBiddingChoiceType[choiceType]);

        const playDirection: GamePlayDirection = this.data.options.gamePlayDirection;
        // let player say choiceType
        const player: Player = this.table.players.GetPlayerByIndex(this.data.lastPlayedPlayerIndex);
        let bubbleText: string = "";
        let bubbleIcon: TextureData = null;
        if (choiceType == PopupBiddingChoiceType.ACCEPT) {
            const topCardSuit: CardSuit = this.table.tableStackInit.GetLast().suit;
            const locData: SharedLocData = AppLocalization.GetSharedData();
            switch (topCardSuit) {
                case CardSuit.SPADES:
                    this.data.trumpSuit = TrumpSuit.SPADES;
                    // bubbleText = locData.table.declarations.spades;
                    bubbleIcon = GetCardSuitIconTextureStatesData(
                        CardSuit.SPADES, 
                        TrumpIconType.POPUP_CHOOSE_OR_TAKE_SUIT,
                    ).out;
                    break;
                case CardSuit.CLUBS:
                    this.data.trumpSuit = TrumpSuit.CLUBS;
                    // bubbleText = locData.table.declarations.clubs;
                    bubbleIcon = GetCardSuitIconTextureStatesData(
                        CardSuit.CLUBS, 
                        TrumpIconType.POPUP_CHOOSE_OR_TAKE_SUIT,
                    ).out;
                    break;
                case CardSuit.DIAMONDS:
                    this.data.trumpSuit = TrumpSuit.DIAMONDS;
                    // bubbleText = locData.table.declarations.diamonds;
                    bubbleIcon = GetCardSuitIconTextureStatesData(
                        CardSuit.DIAMONDS, 
                        TrumpIconType.POPUP_CHOOSE_OR_TAKE_SUIT,
                    ).out;
                    break;
                case CardSuit.HEARTS:
                    this.data.trumpSuit = TrumpSuit.HEARTS;
                    // bubbleText = locData.table.declarations.hearts;
                    bubbleIcon = GetCardSuitIconTextureStatesData(
                        CardSuit.HEARTS, 
                        TrumpIconType.POPUP_CHOOSE_OR_TAKE_SUIT,
                    ).out;
                    break;
            }
        }
        else if (choiceType == PopupBiddingChoiceType.PASS) {
            bubbleText = AppLocalization.GetSharedData().table.declarations.pass;
        }

        // DEBUG
        // this.data.trumpSuit = TrumpSuit.NOTRUMPS;

        player.Say(bubbleText, bubbleIcon);

        // this.OnFirstTrickStart()

        const simpleDeclarationDuration = OptionsDataManager.GetTiming(AppTimings.simpleDeclarationDuration);
        this.table.scene.time.delayedCall(
            simpleDeclarationDuration,
            () => {
                switch (choiceType) {
                    // accepted face-up card;
                    // deal 1 card from initStack to player
                    case PopupBiddingChoiceType.ACCEPT:
                        // update state vars
                        this.data.playerAttackerIndex = this.data.lastPlayedPlayerIndex;
                        // prep for dealing in 'bidding round 2'
                        this.data.lastPlayedPlayerIndex = this.data.trickDealerIndex;
                        // capture restore point
                        this.data.state = {
                            taskType: TaskTypeBelote.DEAL_TABLE_TO_PLAYER,
                            taskSubType: TaskSubTypeBelote.TOOK_FACEUP_CARD,
                            taskData: {
                                playerIndex: this.data.playerAttackerIndex,
                                cardsCount: 1
                            }
                        }
                        // save restore point immediately to localStorage
                        AppLocalStorage.SetTableData(this.variationId, this.data);
                        // start task
                        this.TaskStart(
                            this.data.state.taskType,
                            {
                                playerIndex: this.data.state.taskData.playerIndex,
                                cardsCount: this.data.state.taskData.cardsCount,
                            }
                        );
                        break;
                    // denied faceup card
                    // try next player TAKE|PASS (until end of round)
                    case PopupBiddingChoiceType.PASS:
                        // update state vars
                        const lastPlayerIndex: number = this.data.lastPlayedPlayerIndex;
                        const nextPlayedPlayerIndex = this.GetFixedPlayerIndex(lastPlayerIndex + playDirection);
                        this.data.lastPlayedPlayerIndex = nextPlayedPlayerIndex;
                        // not made whole round yet
                        if (this.data.trickDealerIndex != lastPlayerIndex) {
                            // capture restore point
                            this.data.state = {
                                taskType: TaskTypeBelote.CHOICE_TAKE_SUIT,
                                taskSubType: TaskSubTypeBelote.SECOND_ROUND_PLAYER_DEALT,
                                // taskData: {}
                            }
                            // save restore point immediately to localStorage
                            AppLocalStorage.SetTableData(this.variationId, this.data);
                            // start task
                            this.TaskStart(
                                this.data.state.taskType
                            );
                        }
                        // dealer dealt to itself, round complete -> ALL PASSED in ROUND1!
                        else {
                            // capture restore point
                            this.data.state = {
                                taskType: TaskTypeBelote.CHOICE_SELECT_SUIT,
                                taskSubType: TaskSubTypeBelote.SECOND_ROUND_PLAYER_DEALT,
                                // taskData: {}
                            }
                            // save restore point immediately to localStorage
                            AppLocalStorage.SetTableData(this.variationId, this.data);
                            // start task
                            this.TaskStart(
                                this.data.state.taskType
                            );
                        }
                        break;
                }
            },
            [],
            this
        );
    }

    /**
     * Player (user or AI) has chosen a {suit|alltrumps|notrumps} or PASSed
     * @param choiceType 
     */
    protected ResolvePlayerChooseSuitChoice(choiceType: PopupBiddingChoiceType): void {
        // console.log("ResolvePlayerChooseSuitChoice", PopupBiddingChoiceType[choiceType]);

        const playDirection: GamePlayDirection = this.data.options.gamePlayDirection;
        // let player say choiceType
        const player: Player = this.table.players.GetPlayerByIndex(this.data.lastPlayedPlayerIndex);
        let bubbleText: string = "";
        let bubbleIcon: TextureData = null;
        const sharedLocData: SharedLocData = AppLocalization.GetSharedData();
        switch (choiceType) {
            case PopupBiddingChoiceType.SPADES:
                this.data.trumpSuit = TrumpSuit.SPADES;
                // bubbleText = locData.table.declarations.spades;
                bubbleIcon = GetCardSuitIconTextureStatesData(
                    CardSuit.SPADES, 
                    TrumpIconType.POPUP_CHOOSE_OR_TAKE_SUIT,
                ).out;
                break;
            case PopupBiddingChoiceType.CLUBS:
                this.data.trumpSuit = TrumpSuit.CLUBS;
                // bubbleText = locData.table.declarations.clubs;
                bubbleIcon = GetCardSuitIconTextureStatesData(
                    CardSuit.CLUBS, 
                    TrumpIconType.POPUP_CHOOSE_OR_TAKE_SUIT,
                ).out;
                break;
            case PopupBiddingChoiceType.DIAMONDS:
                this.data.trumpSuit = TrumpSuit.DIAMONDS;
                // bubbleText = locData.table.declarations.diamonds;
                bubbleIcon = GetCardSuitIconTextureStatesData(
                    CardSuit.DIAMONDS, 
                    TrumpIconType.POPUP_CHOOSE_OR_TAKE_SUIT,
                ).out;
                break;
            case PopupBiddingChoiceType.HEARTS:
                this.data.trumpSuit = TrumpSuit.HEARTS;
                // bubbleText = locData.table.declarations.hearts;
                bubbleIcon = GetCardSuitIconTextureStatesData(
                    CardSuit.HEARTS, 
                    TrumpIconType.POPUP_CHOOSE_OR_TAKE_SUIT,
                ).out;
                break;
            case PopupBiddingChoiceType.NOTRUMPS:
                this.data.trumpSuit = TrumpSuit.NOTRUMPS;
                bubbleText = sharedLocData.general.noTrumpsShortText;
                break;
            case PopupBiddingChoiceType.ALLTRUMPS:
                this.data.trumpSuit = TrumpSuit.ALLTRUMPS;
                bubbleText = sharedLocData.general.allTrumpsShortText;
                break;
            case PopupBiddingChoiceType.PASS:
                bubbleText = sharedLocData.table.declarations.pass;
                break;
        }

        // play say
        player.Say(bubbleText, bubbleIcon);

        // this.OnFirstTrick

        const simpleDeclarationDuration = OptionsDataManager.GetTiming(AppTimings.simpleDeclarationDuration);
        this.table.scene.time.delayedCall(
            simpleDeclarationDuration,
            () => {
                switch (choiceType) {
                    // accepted face-up card;
                    // deal 1 card from initStack to player
                    case PopupBiddingChoiceType.SPADES:
                    case PopupBiddingChoiceType.CLUBS:
                    case PopupBiddingChoiceType.DIAMONDS:
                    case PopupBiddingChoiceType.HEARTS:
                    case PopupBiddingChoiceType.NOTRUMPS:
                    case PopupBiddingChoiceType.ALLTRUMPS:
                        this.data.playerAttackerIndex = this.data.lastPlayedPlayerIndex;
                        // prep for dealing in 'bidding round 2'
                        this.data.lastPlayedPlayerIndex = this.data.trickDealerIndex;
                        // capture restore point
                        this.data.state = {
                            taskType: TaskTypeBelote.DEAL_TABLE_TO_PLAYER,
                            taskSubType: TaskSubTypeBelote.TOOK_FACEUP_CARD,
                            taskData: {
                                playerIndex: this.data.playerAttackerIndex,
                                cardsCount: 1
                            }
                        }
                        // save restore point immediately to localStorage
                        AppLocalStorage.SetTableData(this.variationId, this.data);
                        // start task
                        this.TaskStart(
                            this.data.state.taskType,
                            {
                                playerIndex: this.data.state.taskData.playerIndex,
                                cardsCount: this.data.state.taskData.cardsCount,
                            }
                        );
                        break;
                    // denied faceup card
                    // try next player CHOOSE SUIT|PASS (until end of round)
                    case PopupBiddingChoiceType.PASS:
                        const lastPlayerIndex: number = this.data.lastPlayedPlayerIndex;
                        const nextPlayedPlayerIndex = this.GetFixedPlayerIndex(
                            lastPlayerIndex + playDirection
                        );
                        this.data.lastPlayedPlayerIndex = nextPlayedPlayerIndex;
                        // not made whole round yet
                        if (this.data.trickDealerIndex != lastPlayerIndex) {
                            // capture restore point
                            this.data.state = {
                                taskType: TaskTypeBelote.CHOICE_SELECT_SUIT,
                                taskSubType: TaskSubTypeBelote.FIRST_ROUND_PLAYER_DEALT,
                                // taskData: {}
                            }
                            // save restore point immediately to localStorage
                            AppLocalStorage.SetTableData(this.variationId, this.data);
                            // start task
                            // - let next player choose suit
                            this.TaskStart(
                                this.data.state.taskType,
                            );
                        }
                        // dealer dealt to itself, round complete -> ALL PASSED in ROUND2!
                        else {
                            // ALL PASSED round2 bidding phase. restart table
                            // this.table.GetScene().tweens.killAll();
                            // fade out table
                            this.table.transitionDirection = ScreenTransitionDirection.CENTER_TO_LEFT;
                            this.table.Hide(true, () => {
                                // hide table content
                                this.table.HideContent();
                                // reset table (pool cards, etc)
                                this.table.Reset();
                                this.SetDataNextDeal();
                                this.UpdateViewFromData();
                                // fade in table
                                this.table.transitionDirection = ScreenTransitionDirection.RIGHT_TO_CENTER;
                                this.table.Show(true, () => {
                                    // start next table Deal (Task)
                                    this.Start(false, false);
                                }, (t: number) => Phaser.Math.Easing.Elastic.Out(t, 0.8, 1.2));
                            }, (t: number) => Phaser.Math.Easing.Elastic.In(t, 0.8, 1.2));
                        }
                        break;
                }
            },
            [],
            this
        );
    }

    private OnFirstTrickStart(): void
    {
        // if trump suit was chosen (not a PASS)
        if (this.data.trumpSuit != TrumpSuit.UNDEFINED) {
            // update selectedTrumpView component
            this.table.selectedTrumpView.SetPlayerIndex(this.data.playerAttackerIndex);
            this.table.selectedTrumpView.SetTrumpSuit(this.data.trumpSuit);
            this.table.selectedTrumpView.SetBidValue(null);
            this.table.selectedTrumpView.SetContra(null);

            // selectedTrumpView
            if (!this.table.selectedTrumpView.IsShowing()) {
                this.table.selectedTrumpView.Show();
            }

            // // selectedTrumpView visibility just updated.
            // // refresh tableScore and cardPointsView snapping/Layout
            // this.table.tableScore.SetLayout(this.table.selectedTrumpView.IsShowing() ? TableScoreLayout.SNAP_BOTTOM : TableScoreLayout.ROUNDED);
            // this.table.selectedTrumpView.SetLayout(this.table.tableScore.IsShowing() ? SelectedTrumpViewLayout.SNAP_TOP : SelectedTrumpViewLayout.ROUNDED);

            const cardDeckIndex: number = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.cardDeck).value as number;
            this.table.cardPointsView.SetData(this.data.trumpSuit, cardDeckIndex);

            if (!this.table.cardPointsView.IsShowing()) {
                this.table.cardPointsView.Show();
            }

            // update scoreView and selectedTrumpView (size, layout/snapping) 
            // from screen scale OR visibility change
            this.table.UpdateScoreAndSelectedTrumpViews();
        }
    }

    public override GetTablePhase(): TablePhase {
        if (this.data.initStackCards.length) {
            return TablePhase.BIDDING;
        }
        return TablePhase.PLAY;
    }
}